import axios from 'axios';
import { saveAccessToken, saveUser, clearAuthStorage } from './tokenStorage';
import { SHEET_IDS } from '../core/config';

const MASTER_SHEET_ID = SHEET_IDS.MASTER;

// ============================================================
//  SCHEDULE HELPER
// ============================================================

const parseSchedule = (str) => {
  if (!str || str.trim() === '') return [];
  return str.split(';')
    .filter(part => part.trim() !== '')
    .map(part => {
      const [day, startTime, duration] = part.split('|');
      return {
        day: day || '',
        startTime: startTime || '',
        duration: parseInt(duration, 10) || 0,
      };
    });
};

// ============================================================
//  MASTER SHEET (Organisations & User Directory)
// ============================================================

export const fetchMasterSheetData = async (token) => {
  const ranges = ['Organisations!A2:I', 'UserDirectory!A2:F'];
  const params = new URLSearchParams();
  ranges.forEach(r => params.append('ranges', r));
  params.append('majorDimension', 'ROWS');

  const url = `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values:batchGet?${params.toString()}`;
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  const valueRanges = response.data.valueRanges;
  const organisations = (valueRanges[0]?.values || []).map(row => ({
    organisationId: row[0],
    organisationName: row[1],
    organisationCode: row[2],
    spreadsheetId: row[3],
    status: row[4],
    usersSpreadsheetId: row[5],
    attendanceSpreadsheetId: row[6] || row[3],
    marksSpreadsheetId: row[7] || row[3],
    topicsSpreadsheetId: row[8] || row[3],
  }));

  // ✅ Trim email and role to avoid whitespace issues
  const userDirectory = (valueRanges[1]?.values || []).map(row => ({
    email: (row[0] || '').trim(),
    organisationId: row[1] || '',
    userId: row[2] || '',
    name: row[3] || '',
    surname: row[4] || '',
    role: (row[5] || '').trim(),
  }));

  return { organisations, userDirectory };
};

/**
 * Fetch a user from a specific organisation's Users sheet by email.
 */
export const fetchUserFromOrganisation = async (token, spreadsheetId, email) => {
  console.log('Fetching user from spreadsheet:', spreadsheetId);
  const range = 'Users!A2:T';
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const rows = response.data.values || [];
    const trimmedEmail = email.trim();
    const userRow = rows.find(row => (row[4] || '').trim() === trimmedEmail);
    if (!userRow) {
      throw new Error('User not found in the organisation\'s Users sheet.');
    }
    return {
      userId: userRow[0] || '',
      organisationId: userRow[1] || '',
      name: userRow[2] || '',
      surname: userRow[3] || '',
      email: userRow[4] || '',
      role: userRow[5] || '',
      grade: userRow[6] || '',
      school: userRow[7] || '',
      province: userRow[8] || '',
      district: userRow[9] || '',
      circuit: userRow[10] || '',
      teacher: userRow[11] || '',
      subject: userRow[12] || '',
      googleClassroomLink: userRow[13] || '',
      schedule: parseSchedule(userRow[14] || ''),
      zoomLink: userRow[15] || '',
      zoomPassword: userRow[16] || '',
      status: 'active',
    };
  } catch (error) {
    console.error('Full error response:', error.response?.data);
    throw error;
  }
};

// ============================================================
//  GOOGLE PROFILE
// ============================================================

export const fetchGoogleProfile = async (token) => {
  const { data } = await axios.get('https://www.googleapis.com/oauth2/v2/userinfo', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

// ============================================================
//  LOGIN FLOW
// ============================================================

export const processLoginSuccess = async (tokenResponse) => {
  const token = tokenResponse.access_token;
  saveAccessToken(token);

  const googleUser = await fetchGoogleProfile(token);
  const masterData = await fetchMasterSheetData(token);

  const googleEmail = googleUser.email.trim().toLowerCase();

  // 🔍 DEBUG: Log all directory entries
  console.log('📋 UserDirectory entries:', masterData.userDirectory);
  console.log('🔍 Looking for email:', googleEmail);

  const dirEntry = masterData.userDirectory.find(entry =>
    (entry.email || '').trim().toLowerCase() === googleEmail
  );

  if (!dirEntry) {
    console.error('❌ Email not found in directory. Available emails:',
      masterData.userDirectory.map(e => `"${e.email}"`).join(', ')
    );
    throw new Error('Your email is not registered. Please contact support.');
  }


  // Superadmin bypass
  if (dirEntry.role && dirEntry.role.toLowerCase() === 'superadmin') {
    const finalUser = {
      userId: dirEntry.userId || '',
      organisationId: dirEntry.organisationId || '',
      spreadsheetId: '',
      usersSpreadsheetId: '',
      attendanceSpreadsheetId: '',
      marksSpreadsheetId: '',
      topicsSpreadsheetId: '',
      email: googleUser.email,
      role: 'superadmin',
      status: 'active',
      name: dirEntry.name || googleUser.name,
      surname: dirEntry.surname || '',
      school: '',
      province: '',
      district: '',
      circuit: '',
      teacher: '',
      subject: '',
      grade: '',
      googleClassroomLink: '',
      schedule: [],
      zoomLink: '',
      zoomPassword: '',
      day: '',
      startTime: '',
      duration: 0,
      classes: [],
    };
    saveUser(finalUser);
    return finalUser;
  }

  // For non‑superadmin roles, proceed with organisation check
  const org = masterData.organisations.find(o => o.organisationId === dirEntry.organisationId);
  if (!org) {
    throw new Error('Organisation not found in registry.');
  }

  const usersSheetId = org.usersSpreadsheetId || org.spreadsheetId;
  const userDetails = await fetchUserFromOrganisation(token, usersSheetId, googleUser.email);

  const finalUser = {
    userId: userDetails.userId,
    organisationId: userDetails.organisationId,
    spreadsheetId: org.spreadsheetId,
    usersSpreadsheetId: org.usersSpreadsheetId || org.spreadsheetId,
    attendanceSpreadsheetId: org.attendanceSpreadsheetId || org.spreadsheetId,
    marksSpreadsheetId: org.marksSpreadsheetId || org.spreadsheetId,
    topicsSpreadsheetId: org.topicsSpreadsheetId || org.spreadsheetId,
    email: userDetails.email,
    role: userDetails.role,
    status: userDetails.status || 'active',
    name: userDetails.name || googleUser.name,
    surname: userDetails.surname || '',
    school: userDetails.school || '',
    province: userDetails.province || '',
    district: userDetails.district || '',
    circuit: userDetails.circuit || '',
    teacher: userDetails.teacher || '',
    subject: userDetails.subject || '',
    grade: userDetails.grade || '',
    googleClassroomLink: userDetails.googleClassroomLink || '',
    schedule: userDetails.schedule || [],
    zoomLink: userDetails.zoomLink || '',
    zoomPassword: userDetails.zoomPassword || '',
    day: userDetails.schedule.length > 0 ? userDetails.schedule[0].day : '',
    startTime: userDetails.schedule.length > 0 ? userDetails.schedule[0].startTime : '',
    duration: userDetails.schedule.length > 0 ? userDetails.schedule[0].duration : 0,
    classes: [],
  };

  saveUser(finalUser);
  return finalUser;
};

// ============================================================
//  LOGOUT
// ============================================================

export const logout = () => {
  clearAuthStorage();
};